// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import { useState } from "react";
import { getAwardsVerificationRequest, getVerificationRequest } from "../../slices/publicSlice/certificationVerification";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import { LoadingButton } from "@mui/lab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { contactDetails, disclaimer } from "../../config/constant";
import { AppBaseUrl } from "../../config/config";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useCandidateContact } from "../../utils/useCandidateContact";

const CertificationVerifier = () => {
  const dispatch = useAppDispatch();
  const appState = useAppSelector((state: RootState) => state.verification);
  const certifications = appState?.certifications;
  const awards = appState?.awards;
  const [certificationId, setCertificationId] = useState("");
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [candidate] = useCandidateContact(appState);
  const SubmitRequest = async () => {
    await dispatch(getVerificationRequest(certificationId));
    await dispatch(getAwardsVerificationRequest(certificationId));
  };
  const showDisclaimer = () => {
    isDisclaimerOpen ? setIsDisclaimerOpen(false) : setIsDisclaimerOpen(true);
  };
  return (
    <Container component="main" sx={{ pb: 6, mt: -9 }}>
      {/* Certification Validation area */}
      <Card square sx={{ boxShadow: "2px 5px 10px 0px rgb(0 0 0 / 20%)" }}>
        <CardHeader
          title="WSO2 Certification Verification"
          titleTypographyProps={{ align: "center" }}
          subheaderTypographyProps={{
            align: "center",
          }}
        />
        <CardContent sx={{ mt: -1 }}>
          <Grid
            justifyContent="center"
            alignItems="center"
            container
            direction="row"
            spacing={2}
          >
            <Grid item xs={12} md={6}>
              <TextField
              size="small"
              fullWidth
              onChange={(value) => setCertificationId(value.target.value)}
              label="Enter a valid Certification ID"
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <LoadingButton
                variant="contained"
                loading={appState.state === "loading"}
                onClick={SubmitRequest}
                disabled={certificationId === ""}
                translate="no"
              >
                Verify
              </LoadingButton>
            </Grid>
          </Grid>
          {appState.state === "success" && (
            <>
              <Card variant="outlined" sx={{ marginTop: 5, padding: 3 }}>
                <Grid container alignItems="center" justifyContent="center">
                  <Grid
                    item
                    md={12}
                    sx={{ display: "flex", justifyContent: "center" }}
                    mb={2}
                  >
                    {candidate ? (
                      <>
                        <Typography mr={1} variant="h5">
                          <strong>Verified </strong>
                        </Typography>
                        <CheckCircleOutlineIcon
                          sx={{ color: "green", fontSize: 30 }}
                        />
                      </>
                    ) : (
                      <>
                        <CancelIcon sx={{ color: "red", fontSize: 30 }} />

                        <Typography ml={1} variant="h5">
                          <strong>Verification Failed </strong>
                        </Typography>
                      </>
                    )}
                  </Grid>

                  {candidate ? (
                    <>
                      <>
                        <Typography variant="body1">
                          This official WSO2 validation service certifies that{" "}
                          <strong>
                            {candidate?.firstName} {candidate?.lastName}
                          </strong>{" "}
                          has achieved the following certifications and awards.
                          <Tooltip title="Open in New" placement="top" arrow>
                            <IconButton
                              onClick={() => {
                                window.open(
                                  `${AppBaseUrl}/certificate/${certificationId}`,
                                  "_blank"
                                );
                              }}
                              sx={{ color: "secondary.main" }}
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      </>
                      {certifications.map((item, index) => (
                        <Grid
                          justifyContent="center"
                          alignItems="center"
                          container
                          direction="row"
                          spacing={2}
                          mt={1}
                          key={index}
                        >
                          <Grid item xs={8} md={6}>
                            <Card sx={{ padding: 1 }} variant="outlined">
                              {" "}
                              {item?.displayName
                                ? item?.displayName
                                : item?.name}
                            </Card>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <Card sx={{ padding: 1 }} variant="outlined">
                              {" "}
                              {item?.awardedDate}
                            </Card>
                          </Grid>
                        </Grid>
                      ))}
                      {awards.map((item, index) => (
                        <Grid
                          justifyContent="center"
                          alignItems="center"
                          container
                          direction="row"
                          spacing={2}
                          mt={1}
                          key={index}
                        >
                          <Grid item xs={8} md={6}>
                            <Card sx={{ padding: 1 }} variant="outlined">
                              {" "}
                              {item?.name} - {item?.quarter} {item?.year}
                            </Card>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <Card sx={{ padding: 1 }} variant="outlined">
                              {" "}
                              {item?.date}
                            </Card>
                          </Grid>
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <Grid container justifyContent="center" alignItems="center">
                      <Typography variant="body1">
                        You have entered an invalid Certification ID. Please try
                        again!
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  container
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  spacing={3}
                  sx={{ flexGrow: 1 }}
                >
                  <Grid item>
                    {isDisclaimerOpen ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "secondary.main", cursor: "pointer" }}
                        onClick={showDisclaimer}
                      >
                        Hide Disclaimer
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ color: "secondary.main", cursor: "pointer" }}
                        onClick={showDisclaimer}
                      >
                        Read Disclaimer
                      </Typography>
                    )}
                  </Grid>
                  {isDisclaimerOpen && (
                    <Grid item xs={12} md={12}>
                      <Card
                        variant="outlined"
                        sx={{ padding: 3, backgroundColor: "#fafafa" }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontSize: "12px" }}
                          align="justify"
                        >
                          {disclaimer.para1}
                        </Typography>
                        <Typography
                          mt={1}
                          variant="subtitle2"
                          sx={{ fontSize: "12px" }}
                          align="justify"
                        >
                          {disclaimer.para2}
                        </Typography>
                        <Typography
                          mt={1}
                          variant="subtitle2"
                          sx={{ fontSize: "12px" }}
                          align="justify"
                        >
                          {disclaimer.para3}{" "}
                          <a href={contactDetails.mailTo}>
                            certification@wso2.com
                          </a>
                          .
                        </Typography>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default CertificationVerifier;
